import delve from 'dlv';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { ThemeProvider } from 'styled-components';

import { MATERIAL_TYPE } from '../../Components/Classroom/Content';
import Loading from '../../Components/Loading';
import { ClassroomProvider } from '../../Context/ClassroomContext';
import { withConfig } from '../../Context/ConfigContext';
import LearnCraftVideoPlayerProvider from '../../Context/LearnCraftVideoPlayer';
import MindstampVideoPlayerProvider from '../../Context/MindstampVideoPlayer';
import VideoPlayerProvider from '../../Context/VideoPlayer';
import { useClassroomSession } from '../../Domain/Classroom';
import {
  clearCourseData,
  clearCourseItemData,
  clearCourseOutlineData,
} from '../../Redux/classroom-information/actions';
import { withUserProfile } from '../../Redux/user-profile/hoc';
import getThemeConfig from '../../Util/ThemeConfig';

import ClassroomView from './Classroom.view';
import CourseExpiredModal from './Components/CourseExpiredModal';
import EnterClassroomErrorModal from './Components/EnterClassroomErrorModal';
import ClassroomVerificationHeartbeat from './Container/ClassroomHeartBeat';
import UnAuthorizationModal from './Container/UnAuthorizationModal';
import ClearByLearnEdProvider from './Featured/ClearByLearnEd/Contexts/ClearByLearnEdProvider';
import ClearByOnDemandProvider from './Featured/ClearByOnDemand/Contexts/ClearByOnDemandProvider';

const TYPE_LIST = Object.values(MATERIAL_TYPE);

const getClassroomTheme = (courseData) => {
  const theme = getThemeConfig();
  const primaryThemeColor = delve(
    courseData,
    'sectionThemeColor.primaryColor',
    theme.colors.primary
  );

  const classroomTheme = _.defaultsDeep({}, theme);

  if (primaryThemeColor) {
    classroomTheme.colors.primary = primaryThemeColor;
  }
  return classroomTheme;
};

const getPartnerLogoUrl = (hasClassroomSession, courseData, defaultPartnerLogoUrl) => {
  if (hasClassroomSession) {
    const partnerLogoUrl = delve(courseData, 'partnerLogoUrl', defaultPartnerLogoUrl);
    if (partnerLogoUrl === null) {
      return defaultPartnerLogoUrl;
    }
    return partnerLogoUrl;
  }
  return;
};

const ClassroomBase = ({
  history,
  layoutType,
  allowContentType,
  defaultPartnerLogoUrl,
  defaultOwnerLogoUrl,
  appConfig,
  ...props
}) => {
  const [error, setError] = useState(null);
  const { enterClassroom, hasClassroomSession, courseData, isUnauthorizedUser, logoutCallback } =
    useClassroomSession();

  useEffect(() => {
    if (!hasClassroomSession) {
      const initClassroomSession = async () => {
        const { code, ...error } = await enterClassroom();
        if (code !== '200') {
          setError(error);
        }
      };
      initClassroomSession();
    }
    const nope = () => {};
    const {
      clearCourseData = nope,
      clearCourseItemData = nope,
      clearCourseOutlineData = nope,
    } = props;
    return () => {
      clearCourseData();
      clearCourseItemData();
      clearCourseOutlineData();
    };
  }, []);

  if (isUnauthorizedUser) {
    return <UnAuthorizationModal onLogout={logoutCallback} />;
  }

  if (error) {
    const { subCode } = error;
    if (subCode === 'EXPIRED') {
      return <CourseExpiredModal isActive={subCode === 'EXPIRED'} />;
    }
    return <EnterClassroomErrorModal isActive={subCode === 'OTHERS'} />;
  }

  if (!hasClassroomSession) return <Loading />;

  const classroomTheme = getClassroomTheme(courseData);
  const partnerLogoUrl = getPartnerLogoUrl(hasClassroomSession, courseData, defaultPartnerLogoUrl);

  return (
    <ThemeProvider theme={classroomTheme}>
      <ClassroomView
        history={history}
        variant={layoutType}
        allowContentType={allowContentType}
        partnerLogoUrl={partnerLogoUrl}
        courseOwnerLogoUrl={defaultOwnerLogoUrl}
      />
    </ThemeProvider>
  );
};

const ClassroomRoute = connect(null, {
  clearCourseData,
  clearCourseItemData,
  clearCourseOutlineData,
})(ClassroomBase);

const Classroom = ({ appConfig, userProfile, ...props }) => {
  const courseId = delve(props, ['match', 'params', 'id'], null);
  const routeSubsectionReferId = delve(props, ['match', 'params', 'subsectionReferId'], null);
  const searchParams = new URLSearchParams(props.location.search);
  const querySubsectionId = searchParams.get('subsectionId');
  const querySubsectionReferId = searchParams.get('subsectionReferId');
  const subsectionReferId = querySubsectionReferId || routeSubsectionReferId;
  const isEnableVideoResume = delve(appConfig, ['videoPlayer', 'resumeVideo', 'enable']);
  const classroomLayoutType = delve(appConfig, ['classroom', 'layoutType']);
  const allowContentType = delve(appConfig, ['classroom', 'allowContentType'], []);
  const defaultOwnerLogoUrl = delve(appConfig, ['classroom', 'defaultOwnerLogoUrl'], null);
  const defaultPartnerLogoUrl = delve(appConfig, ['classroom', 'defaultPartnerLogoUrl'], null);
  const enableClassroomVerificationHeartbeat = delve(
    appConfig,
    ['classroom', 'enableClassroomVerificationHeartbeat'],
    false
  );
  const enableClassRoomLearningMode = delve(appConfig, ['classroom', 'enableLearningMode'], false);

  const {
    videoPlayer: { watermarkType },
  } = appConfig;

  const { firstname, lastname, id: userId, additional6: accountNumber } = userProfile || {};

  const overlayAccountNumber = accountNumber ?? '';

  if (!courseId) return null;

  return (
    <ClassroomProvider
      courseId={courseId}
      subsectionReferId={subsectionReferId}
      targetSubsectionId={querySubsectionId}
      isEnableVideoResume={isEnableVideoResume}
      isEnableLearningMode={enableClassRoomLearningMode}
      userProfile={
        userProfile
          ? { userId, displayName: `${firstname} ${lastname}`, phoneNumber: overlayAccountNumber }
          : undefined
      }
    >
      <VideoPlayerProvider>
        <MindstampVideoPlayerProvider>
          <LearnCraftVideoPlayerProvider>
            <ClearByLearnEdProvider>
              <ClearByOnDemandProvider>
                <ClassroomRoute
                  {...props}
                  appConfig={appConfig}
                  layoutType={classroomLayoutType}
                  allowContentType={allowContentType.filter((allowedType) =>
                    TYPE_LIST.includes(allowedType)
                  )}
                  defaultPartnerLogoUrl={defaultPartnerLogoUrl}
                  defaultOwnerLogoUrl={defaultOwnerLogoUrl}
                />
                {enableClassroomVerificationHeartbeat && <ClassroomVerificationHeartbeat />}
              </ClearByOnDemandProvider>
            </ClearByLearnEdProvider>
          </LearnCraftVideoPlayerProvider>
        </MindstampVideoPlayerProvider>
      </VideoPlayerProvider>
    </ClassroomProvider>
  );
};

export default compose(withConfig, withUserProfile)(Classroom);
